import React, { useState } from "react";
import { CreateCommanWay, GetCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

const IRN_BADGE = ({ status }: { status: string }) => {
  const cls =
    status === "ACTIVE"
      ? "bg-green-200 text-green-800"
      : status === "PENDING"
      ? "bg-yellow-200 text-yellow-800"
      : status === "CANCELLED"
      ? "bg-red-200 text-red-800"
      : "bg-gray-200 text-gray-600";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status || "N/A"}</span>;
};

const CnDnDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState<any>(null);

  // Fetch CN/DN detail
  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_cn_dn_detail", id],
    () => GetCommanWay(`/admin/cn-dn/${id}`, {}, {}),
    {
      enabled: !!id,
      onSuccess(data: any) {
        if (data.status == "SUCCESS") {
          setNote(data.data);
        } else {
          UseToast(data.message || "Failed to load details", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Retry IRN
  const { mutate: mutateRetryIRN, isLoading: retryLoading } = useMutation(
    () =>
      CreateCommanWay({
        url: `/admin/cn-dn/${id}/retryIRN`,
        data: {},
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          UseToast("IRN activated successfully", "success");
          refetch();
        } else {
          UseToast(data.message || "Retry failed", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Cancel IRN
  const { mutate: mutateCancelIRN, isLoading: cancelLoading } = useMutation(
    (params: { reason: string; remark: string }) =>
      CreateCommanWay({
        url: `/admin/cn-dn/${id}/cancel`,
        data: { reason: params.reason, remark: params.remark },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          UseToast("IRN cancelled successfully", "success");
          refetch();
        } else {
          UseToast(data.message || "Cancel failed", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const handleCancel = () => {
    Swal.fire({
      title: "Cancel IRN",
      html: `
        <div style="text-align:left;">
          <p><strong>Note:</strong> ${note.note_no}</p>
          <p class="text-sm text-gray-500 mt-1">Must be within 24hrs of generation per NIC rules.</p>
          <label class="block mt-3 mb-1 font-semibold">Reason:</label>
          <select id="swal-reason" class="w-full border border-gray-300 rounded p-2">
            <option value="1">1 - Duplicate</option>
            <option value="2">2 - Data Entry Mistake</option>
          </select>
          <label class="block mt-2 mb-1 font-semibold">Remark (optional):</label>
          <input id="swal-remark" type="text" class="w-full border border-gray-300 rounded p-2" placeholder="Enter remark" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Cancel IRN",
      confirmButtonColor: "#dc2626",
      preConfirm: () => {
        const reason = (document.getElementById("swal-reason") as HTMLSelectElement)?.value;
        const remark = (document.getElementById("swal-remark") as HTMLInputElement)?.value;
        return { reason, remark };
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        mutateCancelIRN({ reason: result.value.reason, remark: result.value.remark });
      }
    });
  };

  if (isLoading || isFetching) return <Spinner />;
  if (!note) return <div className="text-center py-8 text-gray-500">Loading...</div>;

  return (
    <>
      {(retryLoading || cancelLoading) && <Spinner />}

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/billing/cn-dn")} className="text-blue-600 hover:underline text-sm">
              &larr; Back to List
            </button>
            <h1 className="text-xl font-bold">{note.note_no}</h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${
                note.note_type === "CN" ? "bg-blue-200 text-blue-800" : "bg-orange-200 text-orange-800"
              }`}
            >
              {note.note_type === "CN" ? "Credit Note" : "Debit Note"}
            </span>
            <IRN_BADGE status={note.irn_status} />
          </div>
          <div className="flex gap-2">
            {note.irn_status === "PENDING" && (
              <Button
                type="button"
                className="font-semibold font-inter text-white"
                radius="sm"
                color="warning"
                onPress={() => mutateRetryIRN()}
              >
                Retry IRN
              </Button>
            )}
            {note.irn_status === "ACTIVE" && (
              <Button type="button" className="font-semibold font-inter text-white" radius="sm" color="danger" onPress={handleCancel}>
                Cancel IRN
              </Button>
            )}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6 bg-white border rounded-lg p-4">
          <InfoRow label="Customer" value={note._customerId ? `${note._customerId.code} - ${note._customerId.sh}` : "Walk-in"} />
          <InfoRow label="Subbranch" value={note._subbranch?.branch_name || note.subbranch} />
          <InfoRow label="GSTIN" value={note.gstin || "-"} />
          <InfoRow label="Note Date" value={moment(note.note_date).format("DD/MM/YYYY")} />
          <InfoRow label="Reason" value={note.reason} />
          <InfoRow label="Remarks" value={note.remarks || "-"} />
        </div>

        {/* Amounts */}
        <div className="grid grid-cols-4 gap-3 mb-6 bg-white border rounded-lg p-4">
          <AmountBox label="Base Value" value={note.base_value} />
          <AmountBox label="CGST" value={note.cgst ? (note.base_value * note.cgst) / 100 : 0} pct={note.cgst} />
          <AmountBox label="SGST" value={note.sgst ? (note.base_value * note.sgst) / 100 : 0} pct={note.sgst} />
          <AmountBox label="IGST" value={note.igst ? (note.base_value * note.igst) / 100 : 0} pct={note.igst} />
          <AmountBox label="UGST" value={note.ugst ? (note.base_value * note.ugst) / 100 : 0} pct={note.ugst} />
          <AmountBox label="Tax" value={note.tax} highlight />
          <AmountBox label="Total Value" value={note.total_value} highlight />
        </div>

        {/* IRN Details */}
        {(note.irn || note.irn_ack_no) && (
          <div className="mb-6 bg-white border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">IRN Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <InfoRow label="IRN" value={note.irn || "-"} mono />
              <InfoRow label="Acknowledgement No" value={note.irn_ack_no || "-"} />
              <InfoRow label="Acknowledgement Date" value={note.irn_ack_date ? moment(note.irn_ack_date).format("DD/MM/YYYY HH:mm:ss") : "-"} />
              <InfoRow label="Status" value={note.irn_status || "-"} />
            </div>
          </div>
        )}

        {/* Linked Invoices */}
        {note.linked_invoices && note.linked_invoices.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Linked Invoices</h3>
            <table className="w-full" style={{ fontSize: "85%" }}>
              <thead className="bg-gray-200">
                <tr>
                  <th className="border border-slate-200 px-2 py-1">Invoice ID</th>
                  <th className="border border-slate-200 px-2 py-1">Invoice No</th>
                  <th className="border border-slate-200 px-2 py-1 text-right">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {note.linked_invoices.map((inv: any, i: number) => (
                  <tr key={"li" + i} className="hover:bg-gray-50">
                    <td className="border border-slate-200 px-2 text-center">{typeof inv === "object" ? inv.id : inv}</td>
                    <td className="border border-slate-200 px-2 text-center font-mono">
                      {typeof inv === "object" ? inv.invoice_no : "-"}
                    </td>
                    <td className="border border-slate-200 px-2 pr-4 text-right">
                      {typeof inv === "object" ? `₹${Number(inv.total_value || 0).toFixed(2)}` : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Linked AWBs */}
        {note.linked_awbs && note.linked_awbs.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Linked AWBs</h3>
            <table className="w-full" style={{ fontSize: "85%" }}>
              <thead className="bg-gray-200">
                <tr>
                  <th className="border border-slate-200 px-2 py-1">Sr.</th>
                  <th className="border border-slate-200 px-2 py-1">AWB No</th>
                  <th className="border border-slate-200 px-2 py-1 text-right">Base Value</th>
                </tr>
              </thead>
              <tbody>
                {note.linked_awbs.map((awb: any, i: number) => (
                  <tr key={"la" + i} className="hover:bg-gray-50">
                    <td className="border border-slate-200 px-2 text-center">{i + 1}</td>
                    <td className="border border-slate-200 px-2 text-center">
                      {typeof awb === "object" ? (
                        <a target="_blank" href={`/pod/tracking?awb=${awb.awb_no}`} className="text-primary2 hover:underline">
                          {awb.awb_no}
                        </a>
                      ) : (
                        awb
                      )}
                    </td>
                    <td className="border border-slate-200 px-2 pr-4 text-right">
                      {typeof awb === "object" ? `₹${Number(awb.base_value || 0).toFixed(2)}` : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

const InfoRow = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    <span className="text-xs text-gray-500">{label}</span>
    <p className={`text-sm font-medium ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</p>
  </div>
);

const AmountBox = ({ label, value, pct, highlight }: { label: string; value: number; pct?: number; highlight?: boolean }) => (
  <div className={`text-center p-2 rounded ${highlight ? "bg-blue-50 border border-blue-200" : "bg-gray-50"}`}>
    <div className="text-xs text-gray-500">
      {label}
      {pct !== undefined && pct > 0 ? ` (${pct}%)` : ""}
    </div>
    <div className={`text-sm font-semibold ${highlight ? "text-blue-700" : ""}`}>
      ₹{Number(value || 0).toFixed(2)}
    </div>
  </div>
);

export default CnDnDetail;
